import {
  fetchDeleteXeroConnections,
  fetchGetXeroAccounts,
  fetchGetXeroConnections,
  fetchGetXeroIwocapaySellerAccount,
  fetchGetXeroIwocapaySellerBrandingThemes,
} from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { useStateKey } from '../../hooks/useStateKey.hook';

// Xero account types that are valid iwocaPay fee accounts. These are the
// expense-class types; kept in sync with the backend fee-account validation.
const FEE_ACCOUNT_TYPES = ['EXPENSE', 'OVERHEADS', 'DIRECTCOSTS'];

export const unlinkXero = async (stateKey: string, onUnlink: () => void) => {
  const response = await fetchDeleteXeroConnections({
    reason: 'iwocapay_seller',
    stateKey,
  });

  if (response.ok) {
    onUnlink();
  }
};

export const useGetXeroConnections = () => {
  const { stateKey } = useStateKey();

  const { data, error, isLoading } = useQuery({
    queryKey: ['fetchGetXeroConnectionsIwocapaySeller'],
    queryFn: () =>
      fetchGetXeroConnections({
        reason: 'iwocapay_seller',
        stateKey: stateKey!,
      }),
  });

  return { data, error, isLoading };
};

export const useGetXeroAccounts = (type: 'payment' | 'expense') => {
  const { stateKey } = useStateKey();

  const { data, error, isLoading } = useQuery({
    queryKey: ['fetchGetXeroAccounts', type],
    queryFn: async () => {
      const res = await fetchGetXeroAccounts({
        reason: 'iwocapay_seller',
        stateKey: stateKey!,
        query: {
          account_type: type,
        },
      });
      const activeAccounts = res.data?.accounts?.filter(
        (account) => account.status === 'ACTIVE',
      );

      if (type === 'expense') {
        // All three expense-class account types are valid fee accounts (this
        // matches the backend's fee-account write-validation). Filtering to
        // these keeps the dropdown to sensible cost/expense accounts and
        // excludes other Class=="EXPENSE" types like Depreciation. Previously
        // this only allowed EXPENSE, which wrongly hid Overheads and Direct
        // Costs accounts.
        return (
          activeAccounts?.filter((account) =>
            FEE_ACCOUNT_TYPES.includes(account.type ?? ''),
          ) || []
        );
      }

      return activeAccounts;
    },
    enabled: !!stateKey,
  });

  return { data, error, isLoading };
};

export const useGetXeroThemes = () => {
  const { stateKey } = useStateKey();

  const { data, error, isLoading } = useQuery({
    queryKey: ['fetchGetXeroIwocapaySellerBrandingThemes'],
    queryFn: () =>
      fetchGetXeroIwocapaySellerBrandingThemes({ stateKey: stateKey! }),
    enabled: !!stateKey,
  });

  return { data, error, isLoading };
};

export const useIwocapaySellerXeroAccount = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const { stateKey } = useStateKey();

  const { data, error, isLoading } = useQuery({
    queryKey: ['fetchGetXeroIwocapaySellerAccount'],
    queryFn: () => fetchGetXeroIwocapaySellerAccount({ stateKey: stateKey! }),
    enabled: !!stateKey && enabled,
  });

  return { data, error, isLoading };
};
